import { Flex, Icon, Spinner, Text, Tooltip } from '@chakra-ui/react';
import { currenciesApi } from '@texas/api/endpoints/currenciesApi';
import { suppliersApi } from '@texas/api/endpoints/suppliersApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { useMetadata } from '@texas/hooks/metadata/useMetadata';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from './Icons';

interface Props {
  branchId: number;
  currencyId: number | null;
  exchangeRate: number;
  exchangeRateType: 'sales' | 'purchase';
  supplierId?: number;
  customerId?: number;
}

interface RateRowProps {
  label: string;
  referenceRate: number | null | undefined;
  exchangeRate: number;
}

function RateRow({ label, referenceRate, exchangeRate }: RateRowProps) {
  const { t } = useTranslation();

  if (referenceRate == null) return null;

  const diff = exchangeRate - referenceRate;
  const isHigher = diff > 0.00001;
  const isLower = diff < -0.00001;

  const indicatorColor = isHigher
    ? 'green.400'
    : isLower
    ? 'red.400'
    : 'gray.400';

  const tooltipLabel = isHigher
    ? t('currencyRate.higher')
    : isLower
    ? t('currencyRate.lower')
    : t('currencyRate.equal');

  const IndicatorIcon = isHigher
    ? Icons.ChevronUp
    : isLower
    ? Icons.ChevronDown
    : null;

  return (
    <Flex align="center" justify="space-between" gap={4}>
      <Text fontSize="xs" color="gray.400" flexShrink={0}>
        {label}
      </Text>
      <Flex align="center" gap={1}>
        <Text fontSize="sm">{referenceRate}</Text>
        <Tooltip label={tooltipLabel}>
          <Flex align="center">
            {IndicatorIcon ? (
              <Icon as={IndicatorIcon} color={indicatorColor} boxSize={4} />
            ) : (
              <Text fontSize="xs" color={indicatorColor} lineHeight={1}>
                =
              </Text>
            )}
          </Flex>
        </Tooltip>
      </Flex>
    </Flex>
  );
}

export function CurrencyRate({
  branchId,
  currencyId,
  exchangeRate,
  exchangeRateType,
  supplierId,
  customerId,
}: Props) {
  const { t } = useTranslation();
  const {
    useCurrenciesData: {
      findCurrency,
      currenciesInBranch: _currenciesInBranch,
    },
    currenciesData: [, refetchCurrencies],
    branchesData: [, getBranches, , findBranch],
  } = useMetadata();

  const {
    data: customerCurrencies,
    refetch: refetchCustomerCurrencies,
    loading: customerLoading,
  } = useApiResource(
    currenciesApi.getAllCurrenciesMergedWithCustomerCurrencies,
  );

  const {
    data: supplier,
    refetch: refetchSupplier,
    loading: supplierLoading,
  } = useApiResource(suppliersApi.get);

  useEffect(() => {
    refetchCurrencies();
    getBranches();
  }, [refetchCurrencies, getBranches]);

  useEffect(() => {
    if (customerId != null) {
      refetchCustomerCurrencies(customerId);
    }
  }, [customerId, refetchCustomerCurrencies]);

  useEffect(() => {
    if (supplierId != null && exchangeRateType === 'purchase') {
      refetchSupplier(supplierId);
    }
  }, [supplierId, exchangeRateType, refetchSupplier]);

  const branch = findBranch(branchId);
  const currency = currencyId != null ? findCurrency(currencyId) : null;

  // Branch reference rate — the standard rate for this currency in this branch
  const branchRate =
    exchangeRateType === 'sales' ? currency?.sellingRate : currency?.buyingRate;

  // Customer reference rate — their custom selling rate for this currency in this branch
  const customerCurrency =
    customerId != null && currencyId != null
      ? customerCurrencies?.find(
          (c) => c.id === currencyId && c.branchId === branchId,
        )
      : null;
  const customerRate =
    exchangeRateType === 'sales'
      ? customerCurrency?.sellingRate
      : customerCurrency?.buyingRate;

  // Supplier reference rate — custom buying rate if set
  const supplierBranchData =
    supplierId != null && exchangeRateType === 'purchase'
      ? supplier?.branchData.find((d) => d.branchId === branchId)
      : null;

  const supplierRate =
    supplierBranchData?.useCustomBuyingRate &&
    supplierBranchData.buyingRate != null &&
    supplierBranchData.currencyId === currencyId
      ? supplierBranchData.buyingRate
      : null;

  if (!branch || !currency) return null;

  const isLoading =
    (customerId != null && customerLoading) ||
    (supplierId != null && exchangeRateType === 'purchase' && supplierLoading);

  return (
    <Flex
      direction="column"
      gap={2}
      bg="texas.bg.800"
      _light={{ bg: 'gray.50' }}
      p={3}
      borderRadius="md"
    >
      <Flex align="center" justify="space-between">
        <Text fontSize="xs" color="gray.500">
          {t('currencyRate.comparing', {
            rate: exchangeRate,
            currency: currency.code,
            branch: branch.name,
          })}
        </Text>
        {isLoading && <Spinner size="xs" />}
      </Flex>

      <RateRow
        label={t('currencyRate.branchRate')}
        referenceRate={branchRate}
        exchangeRate={exchangeRate}
      />

      {customerId != null && (
        <RateRow
          label={t('currencyRate.customerRate')}
          referenceRate={customerRate}
          exchangeRate={exchangeRate}
        />
      )}

      {supplierId != null &&
        exchangeRateType === 'purchase' &&
        supplierRate !== null && (
          <RateRow
            label={t('currencyRate.supplierRate')}
            referenceRate={supplierRate}
            exchangeRate={exchangeRate}
          />
        )}
    </Flex>
  );
}
